import React, { useState } from "react";
import Cookies from "universal-cookie";
import {
  Avatar,
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  MenuItem,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import CloseButton from "../../Components/CloseButton";
import {
  StyledDialog,
  StyledTextField,
} from "../../Components/EditVehicleDetails/EditDialogShared";
import {
  CancelFormActionButton,
  PrimaryFormLoadingActionButton,
} from "../Table/GenericDraggableTable/TableStyles";
import { updateProfileSettings } from "../../Api/Api";
import { SetSessionExpired, SetTimeZoneChanged } from "../../Actions";
import { useToast } from "../../Hooks/useToast";
import { timezones } from "../../Static/Data/_Common/Timezones";
import FindLocalTimezone from "../../Helper/Operations/FindLocalTimezone";
import { normalizeKey } from "../../Helper/Common/common_helpers";
import { PROFILE_NAME_MAX_LENGTH } from "../../Helper/Profile/profileName";
import {
  buildProfileSettingsPayload,
  getProfileSettingsChanges,
  LANGUAGE_OPTIONS,
  readSavedProfileSettings,
} from "../../Helper/Profile/profileSettings";

// Chrome is the shared form-dialog set (StyledDialog, StyledTextField and the
// form action buttons) that Edit Vehicle Details and Edit Asset use, so this
// dialog reads as the same product. The selects are StyledTextField with
// `select`, which gives them the same border, focus colour and text size as
// the name field.

// The dialog is 560px wide by default; three single-column fields need less.
const DIALOG_PAPER_SX = { "& .MuiDialog-paper": { width: "480px" } };

// StyledTextField pads its <input> to 44px tall, but a select renders a div,
// which kept MUI's 16.5px padding and stood 54px tall beside the name field.
const SELECT_SX = {
  "& .MuiSelect-select": {
    padding: "12px 32px 12px 16px",
    fontSize: "14px",
    lineHeight: "20px",
    minHeight: "20px",
    height: "20px",
  },
};

// StyledTextField's hover and focus border is purple in every state, so a
// disabled select looked editable on hover and an invalid name turned purple,
// not red, while focused. Measured in the browser, not assumed.
const FIELD_STATE_SX = {
  "& .MuiOutlinedInput-root.Mui-disabled fieldset, & .MuiOutlinedInput-root.Mui-disabled:hover fieldset": {
    borderColor: "#E6E6E6",
  },
  "& .MuiOutlinedInput-root.Mui-error fieldset, & .MuiOutlinedInput-root.Mui-error:hover fieldset, & .MuiOutlinedInput-root.Mui-error.Mui-focused fieldset": {
    borderColor: "#D32F2F",
  },
};

const MENU_PROPS = { PaperProps: { sx: { maxHeight: 300, borderRadius: "8px" } } };

const FieldLabel = ({ children }) => (
  <Typography variant="fw400s14c494949" sx={{ display: "block", lineHeight: "24px", mb: 0.5 }}>
    {children}
  </Typography>
);

const OptionText = ({ children }) => (
  <Typography sx={{ fontSize: "14px" }}>{children}</Typography>
);

// Name, language and timezone in one dialog. Only the fields that changed are
// saved: the name and timezone through their APIs, the language in the browser.
function ProfileSettings(props) {
  const cookies = new Cookies();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation(["common"]);
  const { openNotification } = useToast();
  const timezoneChangedFlag = useSelector((state) => state.TimeZoneChanged.value);

  const [initial] = useState(() => ({
    name: cookies.get("name") || "",
    language: i18n.language,
    timezone: cookies.get("timeZone"),
    isAutomaticTimezone: +cookies.get("isAutomaticTimezone") === 1,
  }));
  const [draft, setDraft] = useState(initial);
  const [nameError, setNameError] = useState("");
  const [saveError, setSaveError] = useState("");
  const [saving, setSaving] = useState(false);

  const changes = getProfileSettingsChanges(initial, draft, FindLocalTimezone()?.name);

  const updateDraft = (field, value) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
    setSaveError("");
    if (field === "name") setNameError("");
  };

  // Name and timezone go to the server in one call (both or neither saved). The
  // language is browser-only, so it is applied only once the server save worked.
  const handleSave = async () => {
    if (changes.nameError) {
      setNameError(changes.nameError);
      return;
    }

    setSaving(true);
    const payload = buildProfileSettingsPayload(changes, draft.isAutomaticTimezone);
    if (payload) {
      const res = await updateProfileSettings(payload);
      const code = res?.responseStatus?.code;
      if (code !== 200) {
        setSaving(false);
        if (code === -2) dispatch(SetSessionExpired(true));
        const msg = code === 400 && res?.responseStatus?.msg ? res.responseStatus.msg : "";
        if (msg && payload.name !== undefined && !payload.timezone) setNameError(msg);
        setSaveError(msg || "Could not save your settings. Nothing was changed. Please try again later");
        return;
      }

      const saved = readSavedProfileSettings(res);
      if (payload.name !== undefined) {
        const savedName = saved.name || payload.name;
        cookies.set("name", savedName, { path: "/" });
        props.onNameUpdated?.(savedName);
      }
      if (payload.timezone) {
        const savedTimezone = saved.timezone || payload.timezone;
        cookies.set("timeZone", savedTimezone.timezonename, { path: "/" });
        cookies.set("isAutomaticTimezone", savedTimezone.isAutomaticTime, { path: "/" });
        dispatch(SetTimeZoneChanged(!timezoneChangedFlag));
      }
    }

    if (changes.languageChanged) {
      // Same cookie the old Change Language dialog wrote.
      cookies.set("lang", changes.language);
      i18n.changeLanguage(changes.language);
    }
    setSaving(false);
    openNotification({ text: "Profile settings saved", icon: "toast-success" });
    props.handleCloseProfileSettings();
  };

  const close = () => !saving && props.handleCloseProfileSettings();

  return (
    <StyledDialog
      onClose={close}
      aria-labelledby="profile-settings-dialog-title"
      open={props.openProfileSettings}
      sx={DIALOG_PAPER_SX}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
          <Avatar
            // Same colour as the header avatar (ProfileAvatar).
            sx={{ width: 40, height: 40, bgcolor: "#FF977A", fontSize: "16px", fontWeight: 600 }}
            data-testid="profile-settings-avatar"
          >
            {(changes.name || initial.name || "?").charAt(0)}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="fw600s16c000000"
              id="profile-settings-dialog-title"
              sx={{ display: "block", fontSize: "20px", lineHeight: "24px", color: "#101111" }}
            >
              {t("profile.profile_settings", "Profile Settings")}
            </Typography>
            <Typography sx={{ fontSize: "12px", lineHeight: "18px", color: "#8D8D8D" }}>
              Your name, language and time zone
            </Typography>
          </Box>
        </Box>
        <CloseButton onClick={close} aria-label="Close" data-testid="profile-settings-close" />
      </Box>

      <Divider sx={{ my: 3, borderColor: "#E6E6E6" }} />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Box>
          <FieldLabel>Name</FieldLabel>
          <StyledTextField
            id="profile-settings-name"
            fullWidth
            sx={FIELD_STATE_SX}
            value={draft.name}
            error={Boolean(nameError)}
            onChange={(e) => updateDraft("name", e.target.value)}
            inputProps={{
              maxLength: PROFILE_NAME_MAX_LENGTH,
              "aria-label": "Name",
              "data-testid": "profile-settings-name-input",
            }}
          />
          {nameError && (
            <Box sx={{ mt: 0.5 }}>
              <Typography variant="textSubtitleRed">{nameError}</Typography>
            </Box>
          )}
        </Box>

        <Box>
          <FieldLabel>Language</FieldLabel>
          <StyledTextField
            select
            fullWidth
            value={draft.language}
            onChange={(e) => updateDraft("language", e.target.value)}
            inputProps={{ "aria-label": "Language" }}
            sx={{ ...SELECT_SX, ...FIELD_STATE_SX }}
            SelectProps={{
              MenuProps: MENU_PROPS,
              SelectDisplayProps: { "data-testid": "profile-settings-language" },
            }}
          >
            {LANGUAGE_OPTIONS.map((option) => (
              <MenuItem value={option.value} key={option.value}>
                <OptionText>{option.label}</OptionText>
              </MenuItem>
            ))}
          </StyledTextField>
        </Box>

        <Box>
          <FieldLabel>Time zone</FieldLabel>
          <FormControlLabel
            control={
              <Checkbox
                checked={draft.isAutomaticTimezone}
                sx={{ "& .MuiSvgIcon-root": { fontSize: 20 } }}
                onChange={(e) => updateDraft("isAutomaticTimezone", e.target.checked)}
                inputProps={{ "data-testid": "profile-settings-timezone-auto" }}
              />
            }
            label={
              <Typography sx={{ fontSize: "14px", color: "#494949" }}>
                Set time zone automatically from this browser
              </Typography>
            }
            sx={{ mb: 0.5, ml: "-6px" }}
          />
          <StyledTextField
            select
            fullWidth
            disabled={draft.isAutomaticTimezone}
            value={draft.isAutomaticTimezone ? changes.timezone : draft.timezone}
            onChange={(e) => updateDraft("timezone", e.target.value)}
            inputProps={{ "aria-label": "Time zone" }}
            sx={{ ...SELECT_SX, ...FIELD_STATE_SX }}
            SelectProps={{
              MenuProps: MENU_PROPS,
              SelectDisplayProps: { "data-testid": "profile-settings-timezone" },
            }}
          >
            {timezones.map((item) => (
              <MenuItem value={item.name} key={item.name}>
                <OptionText>
                  ({item.offset}) {t(`timezones.${normalizeKey(item.name)}`, item.name)}
                </OptionText>
              </MenuItem>
            ))}
          </StyledTextField>
        </Box>

        {saveError && <Typography variant="textSubtitleRed">{saveError}</Typography>}
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
        <CancelFormActionButton
          onClick={close}
          disabled={saving}
          data-testid="profile-settings-cancel"
        >
          Cancel
        </CancelFormActionButton>
        <PrimaryFormLoadingActionButton
          onClick={handleSave}
          loading={saving}
          disabled={saving || !changes.hasChanges}
          data-testid="profile-settings-save"
        >
          Save
        </PrimaryFormLoadingActionButton>
      </Box>
    </StyledDialog>
  );
}

export default ProfileSettings;
